import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, MessageSquareText, Mail, Copy, X, CreditCard, ClipboardCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail, trackEvent } from "@/lib/api";
import { WAIVER_TITLE, WAIVER_META, WAIVER_INTRO, WAIVER_SECTIONS, WAIVER_ACKNOWLEDGMENT, WAIVER_CHECKBOX_LABEL } from "@/components/guest/waiverText";

const PAY_METHODS = [
  { key: "zelle", label: "Zelle", field: "pay_zelle", nameField: "pay_zelle_name" },
  { key: "cashapp", label: "Cash App", field: "pay_cashapp", nameField: "pay_cashapp_name" },
  { key: "venmo", label: "Venmo", field: "pay_venmo", nameField: "pay_venmo_name" },
  { key: "paypal", label: "PayPal", field: "pay_paypal", nameField: "pay_paypal_name" },
];

export default function Addons() {
  const [addons, setAddons] = useState([]);
  const [settings, setSettings] = useState({});
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ guest_name: "", delivery_method: "", contact: "", payment_method: "" });
  const [payPopup, setPayPopup] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [ccPopup, setCcPopup] = useState(false);
  const [ccPhone, setCcPhone] = useState("");
  const [ackOpen, setAckOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [waiverSigned, setWaiverSigned] = useState(false);
  const [waiverLegalName, setWaiverLegalName] = useState("");
  const [waiverSignature, setWaiverSignature] = useState("");
  const [waiverChecked, setWaiverChecked] = useState(false);
  const [waiverSignedAt, setWaiverSignedAt] = useState("");
  const [waiverScrolledEnd, setWaiverScrolledEnd] = useState(false);
  const [waiverEmail, setWaiverEmail] = useState("");

  useEffect(() => {
    api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const openReq = (a) => {
    setActive(a); setSent(false); setCcPhone(""); setCcPopup(false); setAckOpen(false); setAcknowledged(false);
    setWaiverOpen(false); setWaiverSigned(false); setWaiverLegalName(""); setWaiverSignature(""); setWaiverChecked(false); setWaiverSignedAt(""); setWaiverScrolledEnd(false); setWaiverEmail("");
    setForm({ guest_name: "", delivery_method: "", contact: "", payment_method: "" });
  };

  const selectPayment = (key) => {
    setForm((f) => ({ ...f, payment_method: key }));
    if (key === "credit_card") setCcPopup(true);
    else setPayPopup(true);
  };

  const submit = async (e, opts = {}) => {
    e?.preventDefault();
    if (!form.guest_name) { toast.error("Please add your name."); return; }
    if (!form.payment_method) { toast.error("Choose a payment method."); return; }
    const isCC = form.payment_method === "credit_card";
    let delivery_method = form.delivery_method;
    let contact = form.contact;
    if (isCC) {
      if (!ccPhone.trim()) { toast.error("Enter a phone number to receive your payment link."); setCcPopup(true); return; }
      delivery_method = "text";
      contact = ccPhone.trim();
    } else {
      if (!delivery_method) { toast.error("Choose how you'd like to receive your access code."); return; }
      if (!contact) { toast.error(delivery_method === "text" ? "Enter your phone number." : "Enter your email."); return; }
    }
    if (active?.returnable_items?.length && !acknowledged && !opts.ack) { setCcPopup(false); setAckOpen(true); return; }
    if (!waiverSigned && !opts.waiver) {
      setCcPopup(false);
      if (!waiverEmail && form.delivery_method === "email" && form.contact.includes("@")) setWaiverEmail(form.contact);
      setWaiverOpen(true);
      return;
    }
    setLoading(true);
    try {
      await api.post("/orders", {
        addon_id: active.id,
        guest_name: form.guest_name,
        delivery_method,
        contact,
        payment_method: form.payment_method,
        waiver_agreed: true,
        waiver_legal_name: waiverLegalName.trim(),
        waiver_signature: waiverSignature.trim(),
        waiver_signed_at: waiverSignedAt || new Date().toISOString(),
        waiver_email: waiverEmail.trim(),
      });
      setSent(true);
      trackEvent("addon_request", active.title, active.title);
      toast.success("Request sent to your host!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const selectedPay = PAY_METHODS.find((m) => m.key === form.payment_method);
  const payInfo = selectedPay ? (settings[selectedPay.field] || "").trim() : "";
  const payName = selectedPay ? (settings[selectedPay.nameField] || "").trim() : "";

  const copyInfo = async () => {
    try { await navigator.clipboard.writeText(payInfo); toast.success("Payment info copied!"); }
    catch { toast.error("Couldn't copy — please copy it manually."); }
  };

  const confirmWaiver = () => {
    if (!waiverScrolledEnd) { toast.error("Please scroll to the bottom of the waiver first."); return; }
    if (!waiverChecked) { toast.error("Please check the box to agree to the waiver."); return; }
    if (!waiverLegalName.trim()) { toast.error("Enter your full legal name."); return; }
    if (!waiverSignature.trim()) { toast.error("Type your signature to sign."); return; }
    const ts = new Date().toISOString();
    setWaiverSignedAt(ts);
    setWaiverSigned(true);
    setWaiverOpen(false);
    submit(null, { ack: true, waiver: true });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <div className="mb-8">
        <span className="overline text-brand-terracotta">Make It Special</span>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2">Add-Ons & Services</h1>
        <p className="text-brand-forest/60 mt-2">Pick an add-on, pay your host directly, and receive an access code to unlock your item.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {addons.map((a, i) => (
          <motion.div
            key={a.id}
            data-testid={`addon-card-${a.id}`}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
            className="bg-white rounded-3xl overflow-hidden border-2 border-brand-terracotta shadow-sm flex flex-col"
          >
            {a.image_url && (
              <button
                type="button"
                data-testid={`addon-image-${a.id}`}
                onClick={() => setZoomImage(mediaUrl(a.image_url))}
                className="h-56 bg-white flex items-center justify-center overflow-hidden cursor-zoom-in"
              >
                <img src={mediaUrl(a.image_url)} alt={a.title} className="max-h-full max-w-full object-contain" />
              </button>
            )}
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-display text-xl tracking-tight">{a.title}</h3>
                {a.price_note && <span className="shrink-0 font-semibold text-brand-terracotta">{a.price_note}</span>}
              </div>
              <p className="text-sm text-brand-forest/70 leading-relaxed flex-1">{a.description}</p>
              {a.card_image_url ? (
                <Button
                  data-testid={`addon-request-${a.id}`}
                  onClick={() => setZoomImage(mediaUrl(a.card_image_url))}
                  className="mt-5 w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"
                >
                  <Sparkles size={16} className="mr-2" /> View Business Card
                </Button>
              ) : a.link_url ? (
                <a
                  href={a.link_url}
                  target="_blank" rel="noreferrer"
                  data-testid={`addon-request-${a.id}`}
                  className="mt-5 w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors inline-flex items-center justify-center font-medium"
                >
                  <Sparkles size={16} className="mr-2" /> Sign Up
                </a>
              ) : (
                <Button
                  data-testid={`addon-request-${a.id}`}
                  onClick={() => openReq(a)}
                  className="mt-5 w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors"
                >
                  <Sparkles size={16} className="mr-2" /> Request
                </Button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Request / Send Request dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="addon-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{active?.title}</DialogTitle>
          </DialogHeader>

          {active?.price_note && !sent && (
            <p className="text-brand-forest/80"><span className="font-semibold text-brand-terracotta text-lg">{active.price_note}</span> — {active.description}</p>
          )}

          {sent ? (
            <div className="py-8 text-center">
              <CheckCircle2 size={52} className="mx-auto text-brand-terracotta mb-4" />
              <p className="text-brand-forest/80">
                {form.payment_method === "credit_card" ? (
                  <>Thanks! Your host will text a secure credit-card payment link to <span className="font-semibold">{ccPhone}</span>. Once you pay, your access code will be sent by text.</>
                ) : (
                  <>Thanks! Your host has been notified. Once your payment is confirmed, your access code will be sent by <span className="font-semibold">{form.delivery_method === "text" ? "text message" : "email"}</span>.</>
                )}
              </p>
              <Button onClick={() => setActive(null)} className="mt-6 rounded-full bg-brand-forest text-white" data-testid="addon-close">Done</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 pt-2">
              <div>
                <Label className="mb-1.5 block">Your Name</Label>
                <Input data-testid="addon-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="h-11 rounded-xl" />
              </div>

              <div className={form.payment_method === "credit_card" ? "hidden" : ""}>
                <Label className="mb-2 block">I want to receive my access code by</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="delivery-text"
                    onClick={() => setForm({ ...form, delivery_method: "text", contact: "" })}
                    className={`h-11 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-colors ${form.delivery_method === "text" ? "border-brand-terracotta bg-brand-terracotta/10 text-brand-terracotta" : "border-black/10 text-brand-forest/70"}`}
                  >
                    <MessageSquareText size={16} /> Text
                  </button>
                  <button
                    type="button"
                    data-testid="delivery-email"
                    onClick={() => setForm({ ...form, delivery_method: "email", contact: "" })}
                    className={`h-11 rounded-xl border-2 flex items-center justify-center gap-2 font-medium transition-colors ${form.delivery_method === "email" ? "border-brand-terracotta bg-brand-terracotta/10 text-brand-terracotta" : "border-black/10 text-brand-forest/70"}`}
                  >
                    <Mail size={16} /> Email
                  </button>
                </div>
                {form.delivery_method && (
                  <Input
                    data-testid="addon-contact"
                    type={form.delivery_method === "text" ? "tel" : "email"}
                    placeholder={form.delivery_method === "text" ? "Your phone number" : "Your email address"}
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    className="h-11 rounded-xl mt-3"
                  />
                )}
              </div>

              <div>
                <Label className="mb-2 block">Payment method</Label>
                <div className="grid grid-cols-2 gap-3">
                  {PAY_METHODS.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      data-testid={`pay-${m.key}`}
                      onClick={() => selectPayment(m.key)}
                      className={`h-11 rounded-xl border-2 flex items-center gap-2 px-3 font-medium transition-colors ${form.payment_method === m.key ? "border-brand-terracotta bg-brand-terracotta/10 text-brand-terracotta" : "border-black/10 text-brand-forest/70"}`}
                    >
                      <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${form.payment_method === m.key ? "border-brand-terracotta bg-brand-terracotta" : "border-black/20"}`} />
                      {m.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="pay-credit_card"
                  onClick={() => selectPayment("credit_card")}
                  className={`mt-3 w-full h-11 rounded-xl border-2 flex items-center gap-2 px-3 font-medium transition-colors ${form.payment_method === "credit_card" ? "border-brand-terracotta bg-brand-terracotta/10 text-brand-terracotta" : "border-black/10 text-brand-forest/70"}`}
                >
                  <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${form.payment_method === "credit_card" ? "border-brand-terracotta bg-brand-terracotta" : "border-black/20"}`} />
                  <CreditCard size={16} /> Credit card
                </button>
                {form.payment_method && selectedPay && (
                  <button type="button" onClick={() => setPayPopup(true)} data-testid="view-pay-info"
                    className="mt-2 text-sm font-medium text-brand-terracotta hover:underline">
                    View {selectedPay?.label} payment details
                  </button>
                )}
                {form.payment_method === "credit_card" && (
                  <button type="button" onClick={() => setCcPopup(true)} data-testid="view-cc-info"
                    className="mt-2 block text-sm font-medium text-brand-terracotta hover:underline">
                    {ccPhone ? `We'll text a payment link to ${ccPhone} — edit` : "Enter phone number for payment link"}
                  </button>
                )}
              </div>

              <Button type="submit" disabled={loading} data-testid="addon-submit" className="w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white active:scale-95 transition-colors">
                {loading ? "Sending…" : "Send Request"}
              </Button>
              <p className="text-xs text-brand-forest/50 text-center">After you pay, your host confirms and sends your access code.</p>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Large payment-info popup */}
      <Dialog open={payPopup} onOpenChange={setPayPopup}>
        <DialogContent className="rounded-3xl max-w-xl" data-testid="pay-info-popup">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl">Pay with {selectedPay?.label}</DialogTitle>
          </DialogHeader>
          <p className="text-brand-forest/70">
            Send your payment to the account below. Double-check the account name before sending, then close this window and tap <span className="font-semibold">Send Request</span>.
          </p>

          {payInfo ? (
            <div className="mt-2 rounded-2xl border-2 border-brand-terracotta bg-brand-sand p-6 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-wide text-brand-forest/50 mb-1">Account name (verify this matches)</p>
                <p data-testid="pay-account-name" className="font-display text-2xl text-brand-forest break-words">{payName || "—"}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-brand-forest/50 mb-1">{selectedPay?.label} payment info</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <p data-testid="pay-account-info" className="font-display text-3xl font-bold text-brand-terracotta break-all">{payInfo}</p>
                  <Button type="button" onClick={copyInfo} data-testid="copy-pay-info"
                    className="rounded-full bg-brand-forest hover:bg-brand-terracotta text-white h-10 px-5">
                    <Copy size={16} className="mr-2" /> Copy
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-2 rounded-2xl bg-brand-sand border border-black/5 p-6">
              <p className="text-brand-forest/70">Your host hasn't added their {selectedPay?.label} details yet — please ask them directly.</p>
            </div>
          )}

          <Button type="button" onClick={() => setPayPopup(false)} data-testid="pay-info-done"
            className="mt-4 w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white">
            Done
          </Button>
        </DialogContent>
      </Dialog>
      {/* Credit-card payment-link popup */}
      <Dialog open={ccPopup} onOpenChange={setCcPopup}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="cc-info-popup">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Pay by credit card</DialogTitle>
          </DialogHeader>
          <p className="text-brand-forest/70">
            We'll send you a secure link by text so you can pay by credit card.
          </p>
          <div className="mt-2">
            <Label className="mb-1.5 block">Phone number to send the link to</Label>
            <Input
              data-testid="cc-phone"
              type="tel"
              inputMode="tel"
              placeholder="e.g. (480) 555-1234"
              value={ccPhone}
              onChange={(e) => setCcPhone(e.target.value)}
              className="h-11 rounded-xl"
            />
          </div>
          <Button
            type="button"
            data-testid="cc-info-done"
            disabled={loading}
            onClick={() => {
              if (!ccPhone.trim()) { toast.error("Please enter a phone number."); return; }
              if (!form.guest_name) { toast.error("Please add your name first."); setCcPopup(false); return; }
              setCcPopup(false);
              submit();
            }}
            className="mt-4 w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send Request"}
          </Button>
          <p className="text-xs text-brand-forest/50 text-center">Your host will be notified right away.</p>
        </DialogContent>
      </Dialog>
      {/* Return-items acknowledgment */}
      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="return-ack-popup">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><ClipboardCheck className="text-brand-terracotta" size={24} /> Please return these items</DialogTitle>
          </DialogHeader>
          <p className="text-brand-forest/70">The <span className="font-semibold">{active?.title}</span> includes items you'll need to return to the locker before you check out:</p>
          <ul className="mt-2 rounded-2xl border-2 border-brand-terracotta bg-brand-sand p-4 space-y-1.5" data-testid="return-items-list">
            {(active?.returnable_items || []).map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-brand-forest"><span className="text-brand-terracotta mt-0.5">•</span> {it}</li>
            ))}
          </ul>
          {active?.keepable_items?.length > 0 && (
            <div className="mt-1" data-testid="keep-items-block">
              <p className="text-xs uppercase tracking-wide text-brand-forest/50 mb-1">Yours to keep</p>
              <ul className="text-sm text-brand-forest/70 space-y-1">
                {active.keepable_items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-forest/40 mt-0.5" /> {it}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-brand-forest/50">We'll also text/email you a reminder the night before checkout.</p>
          <Button
            type="button"
            data-testid="return-ack-confirm"
            onClick={() => { setAcknowledged(true); setAckOpen(false); submit(null, { ack: true }); }}
            className="mt-2 w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white"
          >
            I understand — I'll return these items
          </Button>
        </DialogContent>
      </Dialog>

      {/* Adventure Pack waiver — required before any request is submitted */}
      <Dialog open={waiverOpen} onOpenChange={setWaiverOpen}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="waiver-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl sm:text-2xl flex items-center gap-2">
              <ShieldCheck className="text-brand-terracotta shrink-0" size={24} /> {WAIVER_TITLE}
            </DialogTitle>
          </DialogHeader>

          <div className="text-sm text-brand-forest/80 space-y-1 pt-1">
            <p><span className="font-semibold">Property:</span> {WAIVER_META.property}</p>
            <p><span className="font-semibold">Owner/Operator:</span> {WAIVER_META.owner}</p>
          </div>

          <div
            data-testid="waiver-scroll"
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setWaiverScrolledEnd(true);
            }}
            className="mt-3 rounded-2xl border border-black/10 bg-brand-bg p-4 sm:p-5 max-h-[38vh] overflow-y-auto space-y-4 text-sm leading-relaxed text-brand-forest/85"
          >
            <p>{WAIVER_INTRO}</p>
            {WAIVER_SECTIONS.map((s) => (
              <div key={s.heading}>
                <p className="font-semibold text-brand-forest">{s.heading}</p>
                <p className="whitespace-pre-line mt-1">{s.body}</p>
              </div>
            ))}
            <div className="pt-1 border-t border-black/10">
              <p className="font-semibold text-brand-forest">ACKNOWLEDGMENT</p>
              <p className="mt-1">{WAIVER_ACKNOWLEDGMENT}</p>
            </div>
          </div>

          {!waiverScrolledEnd && (
            <p className="mt-2 text-xs text-brand-terracotta font-medium text-center" data-testid="waiver-scroll-hint">
              Please scroll to the bottom of the waiver to continue.
            </p>
          )}

          <label className={`mt-4 flex items-start gap-3 select-none ${waiverScrolledEnd ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`} data-testid="waiver-agree-label">
            <Checkbox
              checked={waiverChecked}
              disabled={!waiverScrolledEnd}
              onCheckedChange={(v) => setWaiverChecked(!!v)}
              data-testid="waiver-agree-checkbox"
              className="mt-0.5 border-brand-terracotta data-[state=checked]:bg-brand-terracotta data-[state=checked]:border-brand-terracotta"
            />
            <span className="text-sm text-brand-forest/85">{WAIVER_CHECKBOX_LABEL}</span>
          </label>

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="mb-1.5 block">Full Legal Name</Label>
              <Input
                data-testid="waiver-legal-name"
                value={waiverLegalName}
                onChange={(e) => setWaiverLegalName(e.target.value)}
                placeholder="First Middle Last"
                className="h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Signature <span className="text-brand-forest/40">(type your name)</span></Label>
              <Input
                data-testid="waiver-signature"
                value={waiverSignature}
                onChange={(e) => setWaiverSignature(e.target.value)}
                placeholder="Your signature"
                className="h-11 rounded-xl"
                style={{ fontFamily: "cursive" }}
              />
            </div>
          </div>
          <p className="text-xs text-brand-forest/50 mt-2">
            Adventure Pack: <span className="font-medium text-brand-forest/70">{active?.title}</span> · Date/Time recorded automatically when you agree.
          </p>

          <div className="mt-4">
            <Label className="mb-1.5 block">Email a copy of your signed waiver to <span className="text-brand-forest/40">(optional)</span></Label>
            <Input
              data-testid="waiver-email"
              type="email"
              value={waiverEmail}
              onChange={(e) => setWaiverEmail(e.target.value)}
              placeholder="you@email.com"
              className="h-11 rounded-xl"
            />
            <p className="text-xs text-brand-forest/50 mt-1">We'll email you a PDF copy. Your host also keeps a copy on file.</p>
          </div>

          <Button
            type="button"
            data-testid="waiver-agree-submit"
            disabled={loading || !waiverScrolledEnd || !waiverChecked}
            onClick={confirmWaiver}
            className="mt-4 w-full h-11 rounded-full bg-brand-forest hover:bg-brand-terracotta text-white disabled:opacity-60"
          >
            {loading ? "Submitting…" : "Agree & Submit Request"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Full-screen image zoom */}
      {zoomImage && (
        <div
          data-testid="addon-image-zoom"
          onClick={() => setZoomImage(null)}
          className="fixed inset-0 z-[120] bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <button
            type="button"
            data-testid="addon-image-zoom-close"
            onClick={(e) => { e.stopPropagation(); setZoomImage(null); }}
            className="absolute top-5 right-5 h-12 w-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center backdrop-blur-md active:scale-95 transition-colors"
            aria-label="Close"
          >
            <X size={26} />
          </button>
          <img
            src={zoomImage}
            alt="Add-on"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[95vw] object-contain rounded-2xl"
          />
        </div>
      )}
    </div>
  );
}
